import { useCallback, useEffect, useMemo, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { SubscriptionIntervals, SubscriptionPlans } from "@prisma/client";
import Cookies from "js-cookie";
import { useSession } from "next-auth/react";

import Dialog from "@musicfy/components/Dialog";
import Icon from "@musicfy/components/Icon";
import StartCheckoutButton from "@musicfy/components/PaymentButtons/StartCheckoutButton";
import { shouldShowFoundingBlock } from "@musicfy/contants/FoundingOffer";
import { PROMO_POPUP } from "@musicfy/contants/PromoPopup";
import { useFoundingOfferAvailability } from "@musicfy/hooks/useFoundingOfferAvailability";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";

/**
 * In-app promo popup. Copy, delay, promo code, and CTA come from `PROMO_POPUP`.
 * Eligibility stays founding-offer gated (free / Starter / Pro monthly).
 */
const MemorialDayBanner = (): JSX.Element | null => {
  const { status } = useSession();
  const { subscription, isLoading: isSubscriptionLoading } =
    useSubscriptionContext();
  const { seatsAvailable } = useFoundingOfferAvailability();

  const [isBannerOpen, setIsBannerOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  const showFounding = shouldShowFoundingBlock({
    plan: SubscriptionPlans.pro,
    interval: SubscriptionIntervals.year,
    subscriptionPlan: subscription?.plan,
    subscriptionInterval: subscription?.interval,
    isSubscriptionLoading,
    seatsAvailable,
  });

  // Free + founding-eligible upgraders (e.g. Starter). Hide for users already
  // on Pro annual / Studio / Label once plan is known.
  const showBanner = useMemo(() => {
    if (!PROMO_POPUP.enabled) return false;
    if (!isBannerOpen || hasDismissed) return false;
    if (status !== "authenticated") return false;
    if (isSubscriptionLoading) return false;
    return showFounding;
  }, [hasDismissed, isBannerOpen, isSubscriptionLoading, showFounding, status]);

  useEffect(() => {
    if (!PROMO_POPUP.enabled) {
      return;
    }

    const dismissed = Cookies.get(PROMO_POPUP.dismissCookie) === "true";

    if (dismissed) {
      setHasDismissed(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsBannerOpen(true);
    }, PROMO_POPUP.showDelayMs);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = useCallback(() => {
    setHasDismissed(true);
    setIsBannerOpen(false);
    Cookies.set(PROMO_POPUP.dismissCookie, "true", {
      expires: PROMO_POPUP.dismissCookieDays,
    });
  }, []);

  if (!showBanner) {
    return null;
  }

  const discount = PROMO_POPUP.promoCode
    ? { code: PROMO_POPUP.promoCode }
    : undefined;

  return (
    <Dialog
      open={showBanner}
      onClose={handleClose}
      title={PROMO_POPUP.title}
      titleSubtext={PROMO_POPUP.titleSubtext}
      TitleIcon={<AccessTimeIcon />}
      width="450px"
    >
      <div className="font-bold text-28">{PROMO_POPUP.headline}</div>

      <div className="mt-4 text-white/90 font-semibold">
        {PROMO_POPUP.supportText}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex items-center gap-3">
          <Icon name="rotation" className="w-5" />
          <div>Unlimited Generations</div>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="sound" className="w-5" />
          <div>Premium Sound</div>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="cube" className="w-5" />
          <div>Custom Voices</div>
        </div>
        <div className="flex items-center gap-3">
          <Icon name="rocket" className="w-5" />
          <div>Faster Speeds</div>
        </div>
      </div>

      <div className="mt-8">
        <StartCheckoutButton
          priceId={PROMO_POPUP.priceId}
          discount={discount}
          cta={PROMO_POPUP.cta}
          buttonClassName="!w-full"
          origin={PROMO_POPUP.origin}
        />
      </div>
    </Dialog>
  );
};

export default MemorialDayBanner;
